import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, X, User } from "lucide-react";
import api from "../../lib/api";
import ImageUpload from "../../components/ImageUpload";

const empty = { name: "", title: "", photo: "", bio: "", quote: "", order: 0 };

export default function LeadersManager({ onChange }) {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/site");
      setList(data.leaders || []);
    } catch { toast.error("Could not load leaders"); }
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing("new"); setForm({ ...empty, order: list.length + 1 }); };
  const startEdit = (l) => {
    setEditing(l.id);
    setForm({
      name: l.name || "",
      title: l.title || "",
      photo: l.photo || "",
      bio: l.bio || "",
      quote: l.quote || "",
      order: l.order || 0,
    });
  };
  const cancel = () => { setEditing(null); setForm(empty); };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        order: Number(form.order) || 0,
        photo: form.photo || null,
        quote: form.quote || null,
      };
      if (editing === "new") await api.post("/admin/leaders", payload);
      else await api.patch(`/admin/leaders/${editing}`, payload);
      toast.success("Saved");
      cancel(); load(); onChange && onChange();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const remove = async (l) => {
    if (!window.confirm(`Delete leader "${l.name}"?`)) return;
    try { await api.delete(`/admin/leaders/${l.id}`); toast.success("Deleted"); load(); onChange && onChange(); }
    catch { toast.error("Delete failed"); }
  };

  if (editing !== null) {
    return (
      <div data-testid="leader-editor" className="border border-[#1F3550] bg-[#0D1B2A] p-6 lg:p-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-heading font-bold text-xl text-[#F8FAFC]">{editing === "new" ? "New Leader" : "Edit Leader"}</h3>
          <button onClick={cancel} data-testid="leader-cancel" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#94A3B8] hover:text-[#F8FAFC]"><X className="h-4 w-4" /> Cancel</button>
        </div>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label>Name *</Label><Input data-testid="ldr-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="e.g. Mr. Rajesh Kumar" /></div>
          <div><Label>Title *</Label><Input data-testid="ldr-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required placeholder="Founder & Chairman" /></div>
          <div><Label>Display Order</Label><Input type="number" data-testid="ldr-order" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} /></div>
          <div className="md:col-span-2">
            <ImageUpload label="Photo" value={form.photo} onChange={(url) => setForm((f) => ({ ...f, photo: url }))} testid="ldr-photo" />
          </div>
          <div className="md:col-span-2">
            <Label>Bio *</Label>
            <textarea
              data-testid="ldr-bio"
              value={form.bio}
              onChange={(e) => setForm({ ...form, bio: e.target.value })}
              required
              rows={6}
              placeholder="3–5 lines about the leader's background, experience and role."
              className="mt-2 w-full bg-[#050A0F] border border-[#1F3550] text-[#F8FAFC] focus:border-[#F39C12] focus:ring-1 focus:ring-[#F39C12] px-4 py-2.5 outline-none"
            />
          </div>
          <div className="md:col-span-2">
            <Label>Quote / Message (optional)</Label>
            <textarea
              data-testid="ldr-quote"
              value={form.quote}
              onChange={(e) => setForm({ ...form, quote: e.target.value })}
              rows={4}
              placeholder="A short message or vision quote that will appear in highlighted style."
              className="mt-2 w-full bg-[#050A0F] border border-[#1F3550] text-[#F8FAFC] focus:border-[#F39C12] focus:ring-1 focus:ring-[#F39C12] px-4 py-2.5 outline-none"
            />
          </div>
          <div className="md:col-span-2">
            <button type="submit" disabled={saving} data-testid="ldr-save" className="inline-flex items-center gap-2 bg-[#F39C12] text-[#050A0F] px-6 py-2.5 font-bold uppercase tracking-widest text-xs hover:bg-[#D68910] disabled:opacity-50">
              <Save className="h-4 w-4" /> {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="leaders-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F39C12]">Leadership</div>
          <h2 className="mt-1 font-heading font-bold text-xl text-[#F8FAFC]">{list.length} leader{list.length === 1 ? "" : "s"}</h2>
        </div>
        <button onClick={startCreate} data-testid="ldr-new" className="inline-flex items-center gap-2 bg-[#F39C12] text-[#050A0F] px-5 py-2.5 font-bold uppercase tracking-widest text-xs hover:bg-[#D68910]"><Plus className="h-4 w-4" /> New Leader</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((l) => (
          <div key={l.id} data-testid={`ldr-row-${l.id}`} className="border border-[#1F3550] bg-[#0D1B2A] overflow-hidden flex flex-col">
            <div className="aspect-[4/5] bg-[#050A0F] flex items-center justify-center">
              {l.photo ? <img src={l.photo} alt={l.name} className="w-full h-full object-cover" /> : <User className="h-20 w-20 text-[#1F3550]" />}
            </div>
            <div className="p-5 flex-1 flex flex-col">
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#F39C12]">{l.title}</div>
              <h3 className="mt-2 font-heading font-bold text-[#F8FAFC] leading-tight">{l.name}</h3>
              <p className="mt-2 text-sm text-[#94A3B8] line-clamp-3">{l.bio}</p>
              <div className="mt-auto pt-4 flex gap-2">
                <button onClick={() => startEdit(l)} data-testid={`ldr-edit-${l.id}`} className="flex-1 border border-[#1F3550] hover:border-[#F39C12] text-[#F8FAFC] py-1.5 text-[10px] uppercase tracking-widest font-bold transition-colors">Edit</button>
                <button onClick={() => remove(l)} data-testid={`ldr-delete-${l.id}`} className="border border-[#1F3550] hover:border-[#EF4444] hover:text-[#EF4444] text-[#94A3B8] px-3 transition-colors"><Trash2 className="h-3 w-3" /></button>
              </div>
            </div>
          </div>
        ))}
        {list.length === 0 && <div className="col-span-full py-12 text-center text-[#94A3B8] border border-dashed border-[#1F3550]">No leaders yet.</div>}
      </div>
    </div>
  );
}

function Label({ children }) { return <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#94A3B8]">{children}</label>; }
function Input(props) { return <input {...props} className="mt-2 w-full bg-[#050A0F] border border-[#1F3550] text-[#F8FAFC] focus:border-[#F39C12] focus:ring-1 focus:ring-[#F39C12] px-4 py-2.5 outline-none" />; }
